import { Component } from '@theme/component';
import { prefersReducedMotion, isMobileBreakpoint } from '@theme/utilities';

/**
 * Reveals the project cards hidden behind the "See more" button, with a staggered fade-in.
 * Cards beyond the current breakpoint's initial count are hidden via CSS until `data-expanded="true"`.
 *
 * @typedef {Object} FeaturedWorkRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} [label]
 * @property {HTMLElement[]} [cards]
 * @extends {Component<FeaturedWorkRefs>}
 */
class FeaturedWorkComponent extends Component {
  #expanded = false;
  #staggerStep = 60;

  get #initialCount() {
    const value = isMobileBreakpoint() ? this.dataset.initialCountMobile : this.dataset.initialCount;
    return parseInt(value, 10) || 0;
  }

  toggle = (event) => {
    event.preventDefault();

    this.#expanded = !this.#expanded;
    this.dataset.expanded = this.#expanded ? 'true' : 'false';
    this.refs.button.setAttribute('aria-expanded', String(this.#expanded));

    if (this.refs.label) {
      this.refs.label.textContent = this.#expanded
        ? this.dataset.lessLabel || 'See less'
        : this.dataset.moreLabel || 'See more';
    }

    if (this.#expanded) this.#revealExtras();
  };

  #revealExtras() {
    if (prefersReducedMotion() || !Array.isArray(this.refs.cards)) return;

    const extras = this.refs.cards.slice(this.#initialCount);

    extras.forEach((card, index) => {
      card.style.setProperty('--fw-reveal-delay', `${index * this.#staggerStep}ms`);
      card.classList.add('featured-work-card--reveal');
      card.addEventListener(
        'animationend',
        () => {
          card.classList.remove('featured-work-card--reveal');
          card.style.removeProperty('--fw-reveal-delay');
        },
        { once: true }
      );
    });
  }
}

if (!customElements.get('featured-work-component')) {
  customElements.define('featured-work-component', FeaturedWorkComponent);
}
